import { watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { doc, onSnapshot } from 'firebase/firestore';
import { projectFirestore, projectAuth } from '@/firebase/config';
import { token, userToken, uidToken } from '@/stores/token';
import { trace } from '@/utils/authTrace';

// Phase 3 (2026-08-25, retired-staff authz) — the visible half.
//
// The rules change is what actually revokes access, and it is instant: rules
// evaluate live data on every operation, so a retired person's writes fail the
// moment they are attempted regardless of how old the session is. This composable
// does not add security. What it adds is an ANSWER: without it, a retired person
// keeps looking at an app that has silently stopped working, and every action
// they take fails for reasons they cannot see.
//
// WHY IT IS A SIBLING OF useIdleLogout RATHER THAN PART OF IT.
// The brief nominated useIdleLogout as the cheapest home, and it is the right
// MOUNT POINT — app root, already holds the real Auth0 logout, already tears down
// cleanly. But idle-logout answers "has this person stopped using the app" and
// this answers "has this person's access been taken away". Two unrelated triggers
// that happen to share an ending. Kept separate so useIdleLogout's timers and its
// existing tests stay untouched; mounted beside it in App.vue, so the cost and
// the lifecycle are identical to putting it inside.
//
// COST: one document listener per session — one read on attach, one per change
// to that person's own document. Negligible next to the three collection
// listeners the Calendar alone attaches.
//
// -- L2, THE ORDERING TRAP -------------------------------------------------
// Once the rules enforce `retired`, this listener's OWN read of the person
// document is denied — `people` read is gated on isOrgMember(), which is exactly
// what a retired person now fails. So it never sees `retired: true`; it gets a
// permission-denied on the error callback instead.
//
// A listener written only against `retired === true` would therefore work
// perfectly right up until the rules deploy, and then silently never fire again.
// Both triggers are required, and both are tested
// (src/tests/sessionRevocationWatch.test.js).
//
// In production the ERROR path is the one that will actually fire. The
// `retired: true` path covers the window where the client has shipped but the
// rules have not — hosting deploys automatically on merge, rules are a separate
// manual deploy — and it is the only thing that behaves correctly in that gap.

// Only this one means "your access is gone", and only once the precondition
// below holds. Everything else — `unavailable`, a transient network failure —
// must NOT sign anyone out: throwing a manager out of the app mid-service
// because of a blip would be its own outage. Phase 2's DataHealthBanner covers
// those with a non-blocking "couldn't refresh" strip.
//
// F1.3 (2026-08-30) — 'unauthenticated' REMOVED. It is literally the
// not-yet-bridged code: Firestore reports it when the request carried no
// credential at all, which is precisely the state this listener used to attach
// in. It was never a revocation signal; it was the defect's own error code
// sitting on the revocation list.
const REVOCATION_CODES = ['permission-denied'];

export function useSessionRevocationWatch() {
  const { isAuthenticated } = useAuth0();
  const route = useRoute();
  const router = useRouter();
  const store = token();
  const uT = userToken();
  const uidStore = uidToken();

  let unsubscribe = null;
  let unsubscribeAuthState = null;
  let loggingOut = false;

  const stopWatching = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };

  // F1.4 (2026-08-30) — LAND, DON'T EJECT.
  //
  // This used to call the real Auth0 logout() directly. Two problems with that
  // ending, and only the second is cosmetic.
  //
  //  1. It is not what the rest of the app does with the same fact.
  //     combinedGuard (router/index.js) and usePostLoginHandler's 403 branch
  //     both route a CONFIRMED loss of access to no-organisation?reason=retired
  //     — the "Access removed" screen, which explains what happened and offers
  //     sign-out as the user's own click. Three code paths, one fact, one
  //     screen.
  //  2. A bare logout lands on the login page, where Auth0 re-authenticates a
  //     retired person perfectly happily — Auth0 knows nothing about
  //     retirement. So the ejection reads as a glitch rather than an answer,
  //     which is the exact failure this composable was written to prevent.
  //
  // It also changes the cost of being WRONG. A wrong logout can only be undone
  // by re-authenticating; a wrong landing is undone by navigating. Given this
  // decision is made from an error code, recoverable-by-navigation is the right
  // direction to fail in.
  //
  // The store writes mirror usePostLoginHandler's 403 branch exactly: this is a
  // positive, server-confirmed access-loss (the precondition below guarantees
  // the request carried a valid Firebase credential), so resolved-none is
  // correct and stale org/permission state must not survive it.
  const revoke = (reason) => {
    // The listener can deliver several events before the navigation settles,
    // and a double revoke produces a visibly broken transition. Latch it.
    if (loggingOut) return;
    loggingOut = true;
    trace('session-revoked', { reason });
    stopWatching();
    store.organisationIdChange('');
    store.orgResolutionChange('resolved-none');
    uT.permissionsChange('');
    // replace(), not push() — a terminal landing, not a step in a flow the user
    // should be able to navigate back out of. no-organisation is guarded by
    // isAuthenticated ONLY (noOrganisationGuard), so nothing downstream bounces
    // an authenticated user off it.
    router.replace({ name: 'no-organisation', query: { reason: 'retired' } });
  };

  // The org to watch, in order of reliability.
  //
  // The Pinia store covers every manager-facing route. It is deliberately NOT
  // the only source: `clock-in` and `my-shifts` never populate it (see
  // mintFirebaseToken's own comment on why the bridge takes no organisationId),
  // and those are the shared-terminal routes where a stale session matters most.
  //  - clock-in is a kiosk URL carrying ?org=<id>, so the route has it.
  //  - my-shifts resolves its org asynchronously into a local ref, so neither
  //    the store nor the route has it — but the Firebase ID token minted by
  //    mintFirebaseToken carries an `orgId` custom claim, which is readable
  //    synchronously from the current user's token result.
  const resolveOrgId = async () => {
    if (store.organisationId) return store.organisationId;
    if (route?.query?.org) return String(route.query.org);
    // F1.2 (2026-08-30) — no optional chain on currentUser any more.
    //
    // This used to read `projectAuth.currentUser?.getIdTokenResult()`, which
    // collapsed two completely different situations into the same `null`:
    // "Firebase has not signed in yet" and "signed in, but the token carries no
    // orgId claim". The first is a readiness signal this function already held
    // and threw away; the second is a real absence.
    //
    // It is now a PRECONDITION instead of a fallback — startWatching() below
    // refuses to run at all unless currentUser matches the uid we are watching,
    // so by the time we get here currentUser is guaranteed present and a null
    // return means only the second thing.
    try {
      const result = await projectAuth.currentUser.getIdTokenResult();
      return result?.claims?.orgId || null;
    } catch (err) {
      // A token read failing is not itself proof of revocation.
      console.error('useSessionRevocationWatch: could not read the ID token claims:', err);
      return null;
    }
  };

  const startWatching = async () => {
    stopWatching();
    if (!isAuthenticated.value || loggingOut) return;

    const uid = uidStore.uid;
    if (!uid) return;

    // -- F1.1, THE PRECONDITION ---------------------------------------------
    //
    // A bug in a precondition costs a delayed revocation notice.
    // A bug in a classifier costs a mass sign-out.
    //
    // THE DEFECT THIS FIXES (dev, 2026-08-30). This composable is mounted in
    // App.vue's setup(), before the first navigation resolves, and it gated on
    // two PERSISTED Pinia values only — `uidStore.uid` and
    // `store.organisationId`, both `persist: true`, both surviving in
    // localStorage from an earlier session. So on a boot where Firebase had not
    // yet been bridged, it attached a Firestore listener with no credential
    // attached at all, took the resulting permission-denied as proof of
    // revocation, and logged the user out MID-LOGIN — while
    // usePostLoginHandler's own ensureFirebaseAuth() was still in flight. The
    // captured trace shows session-revoked arriving between
    // handleUserLogin-start and any handleUserLogin-firebase-bridge-done, and
    // the "Failed to fetch" that followed is the mint request being torn down
    // by the redirect.
    //
    // WHY THIS EXACT CHECK, AND WHY IT IS SUFFICIENT.
    // isOrgMember() in firestore.rules is three conjuncts:
    //
    //   request.auth != null &&
    //   exists(.../people/$(request.auth.uid)) &&
    //   get(.../people/$(request.auth.uid)).data.get('retired', false) != true
    //
    // It consults NO token claim — mintFirebaseToken's own comment says so of
    // the orgId claim it mints ("informational (rules use exists(), not the
    // claim)"). So the only thing that can be missing before the bridge lands
    // is the FIRST conjunct, and `projectAuth.currentUser?.uid === uid` is that
    // conjunct's exact client-side mirror: currentUser is the identity the
    // Firestore SDK will attach, and the custom token is minted with
    // uid === Auth0 sub, which is what uidStore holds. Conjuncts 2 and 3 are
    // Firestore document state, not session state; they cannot be "not ready".
    //
    // WHY NOT AWAIT THE BRIDGE INSTEAD. ensureFirebaseAuth() returns early and
    // silently when there is no Auth0 user, which is indistinguishable from
    // success to every caller; it can also throw a 403. And it is point-in-time
    // — it says the bridge ran once, not that a session exists now.
    //
    // WHY NOT DISCRIMINATE ON THE ERROR CODE. All three conjuncts above fail
    // with the identical `permission-denied`. The distinction was never encoded
    // in the code, so no enumeration can recover it, and any enumeration
    // silently readmits every new cause: a fourth conjunct, a rules-deploy that
    // lands before the client, a different SDK code.
    //
    // FAILURE DIRECTION. Uncertainty now produces NO listener — which is the
    // state this app shipped in for months, and is merely a delayed answer.
    // Previously, uncertainty produced the most destructive action in the app.
    if (projectAuth.currentUser?.uid !== uid) return;

    const orgId = await resolveOrgId();
    if (!orgId) return;

    // Watched at people/{uid}, not by a query on the `uid` FIELD, because that
    // is the exact document firestore.rules' isOrgMember() checks —
    // exists(.../people/$(request.auth.uid)). Watching anything else could
    // report "active" for a document the rules never consult.
    const personRef = doc(projectFirestore, `organisations/${orgId}/people`, uid);

    unsubscribe = onSnapshot(
      personRef,
      (snap) => {
        // Pre-rules-deploy path: the read still succeeds and the flag is visible.
        if (snap.exists() && snap.data()?.retired === true) {
          revoke('retired-flag');
        }
        // A missing document is not handled here, deliberately and with no
        // comment describing a guard: `snap.exists()` is false and nothing
        // happens, which is the whole behaviour. The carve-out that used to be
        // documented on this spot — "the CSV-import shape legitimately has no
        // document at people/{sub}" — was UNREACHABLE. It sat on the snapshot
        // callback, which only runs when the read was ALLOWED, and a read is
        // only allowed when isOrgMember() passed, which requires the very
        // document the carve-out was written for the absence of. A comment
        // describing a guard that cannot fire is worse than no comment: it
        // tells the next reader the case is handled.
      },
      (err) => {
        // L2 path, and the one that fires in production once the rules land.
        if (REVOCATION_CODES.includes(err?.code)) {
          revoke(err.code);
          return;
        }
        console.error('useSessionRevocationWatch: person listener error:', err?.code || err);
      }
    );
  };

  onMounted(() => {
    // F1.1 — the precondition needs its own trigger, or the listener simply
    // never attaches on the boot where Firebase signs in after mount (which is
    // every boot: the bridge is a network round trip). The four watchers below
    // cover identity and org CHANGES; this covers the bridge LANDING, which is
    // the event that flips the precondition from false to true.
    unsubscribeAuthState = projectAuth.onAuthStateChanged(() => {
      startWatching();
    });
    startWatching();
  });

  // Re-attach when the identity, the org or the route's org changes — a user who
  // logs in after the app has mounted, or a kiosk that lands on ?org= later,
  // must still be watched.
  const stopWatchers = [
    watch(isAuthenticated, (authed) => (authed ? startWatching() : stopWatching())),
    watch(() => uidStore.uid, () => startWatching()),
    watch(() => store.organisationId, () => startWatching()),
    watch(() => route?.query?.org, () => startWatching()),
  ];

  onUnmounted(() => {
    stopWatching();
    if (unsubscribeAuthState) {
      unsubscribeAuthState();
      unsubscribeAuthState = null;
    }
    stopWatchers.forEach((stop) => stop());
  });

  return { startWatching, stopWatching };
}
